// Account → My songs: every song the user submitted, coming up first (with local play times or when
// the time gets fixed), then the ones that played (with their ratings); review, hidden, removed and
// rejected songs say so, with the reason.
import { useEffect, useState } from "react";
import { api } from "../api";
import { Link } from "../lib/router";
import { formatPlayTime } from "../river/slots";
import { streamHref } from "../stream";

interface MySong {
  id: string;
  title: string | null;
  bpm: number;
  day: number;
  status: "approved" | "review" | "hidden" | "removed" | "rejected";
  reason: string | null;
  playsAt: number | null;
  freezesAt: number;
  played: boolean;
  wizardShare: number;
  rating: { average: number | null; count: number } | null;
}

const STATUS: Record<MySong["status"], string> = {
  approved: "",
  review: "Waiting for review",
  hidden: "Hidden after reports",
  removed: "Removed by an admin",
  rejected: "Not accepted in review",
};

function When({ s }: { s: MySong }) {
  if (s.status !== "approved") return <span className="muted">—</span>;
  if (s.playsAt) return <>{formatPlayTime(s.playsAt)}</>;
  return <span className="muted">Time fixed {formatPlayTime(s.freezesAt)}</span>;
}

function SongRow({ s }: { s: MySong }) {
  return (
    <tr data-testid="my-song">
      <td>
        {s.status === "approved" ? (
          <Link href={`/loop/${s.id}`}>{s.title ?? "Untitled"}</Link>
        ) : (
          (s.title ?? "Untitled")
        )}
        {s.bpm === 72 && <span className="chip">72</span>}
        {STATUS[s.status] && (
          <div className="small muted">
            {STATUS[s.status]}
            {s.reason ? `: ${s.reason}` : ""}
          </div>
        )}
      </td>
      <td>
        <When s={s} />
      </td>
      <td>
        {s.rating?.count ? (
          <>
            ★ {s.rating.average?.toFixed(1)} <span className="muted">({s.rating.count})</span>
          </>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
    </tr>
  );
}

function SongTable({ songs, caption }: { songs: MySong[]; caption: string }) {
  return (
    <table className="my-songs">
      <caption className="sr-only">{caption}</caption>
      <thead>
        <tr>
          <th scope="col">Song</th>
          <th scope="col">Plays (your time)</th>
          <th scope="col">Rating</th>
        </tr>
      </thead>
      <tbody>
        {songs.map((s) => (
          <SongRow key={s.id} s={s} />
        ))}
      </tbody>
    </table>
  );
}

export function MySongs() {
  const [songs, setSongs] = useState<MySong[] | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    api<{ songs: MySong[] }>("GET", "/api/me/songs")
      .then((r) => setSongs(r.songs))
      .catch(() => setError(true));
  }, []);
  const upcoming = (songs ?? [])
    .filter((s) => !s.played)
    .sort((a, b) => (a.playsAt ?? a.freezesAt) - (b.playsAt ?? b.freezesAt));
  const past = (songs ?? []).filter((s) => s.played);
  return (
    <section className="panel stack" aria-labelledby="songs-h" data-testid="my-songs">
      <h2 id="songs-h">My songs</h2>
      {error && <p role="alert">Couldn't load your songs.</p>}
      {songs && songs.length === 0 && (
        <p>
          You haven't submitted a song yet. <Link href="/calendar">Pick a day in the calendar</Link> and write one — or
          try the <a href={streamHref(72, "/calendar")}>72 BPM site</a>.
        </p>
      )}
      {upcoming.length > 0 && (
        <>
          <h3>Coming up</h3>
          <SongTable songs={upcoming} caption="Your songs that haven't played yet" />
        </>
      )}
      {past.length > 0 && (
        <>
          <h3>Played</h3>
          <SongTable songs={past} caption="Your songs that have played" />
        </>
      )}
    </section>
  );
}
